import React from 'react';

import { SettingSectionTitle } from '@/components/shared/SettingCard';
import { SongCard, SongCardSong } from '@/components/shared/SongCard';
import { useGetUserTopTeamSongsQuery } from '@/graphql';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useLayout } from '@/providers/LayoutProvider';

import styles from './UserTopSongs.module.scss';

export interface UserTopSongsProps {
	userId: string;
	variant: 'dashboard' | 'profile';
}

export const UserTopSongs: React.FC<UserTopSongsProps> = ({
	userId,
	variant,
}) => {
	const { user } = useAuth();
	const { openSong } = useLayout();
	const fireOpenSong = useAnalyticsEvent('User_Top_Songs_Open_Song');
	const { data, isLoading, isError } = useGetUserTopTeamSongsQuery(
		{ userId },
		{ enabled: !!userId },
	);

	const topSongs = data?.getUserTopTeamSongs;

	if (isLoading || isError || !topSongs?.length) {
		return null;
	}

	const isSelf = user?.id === userId;
	const isDashboard = variant === 'dashboard';

	const songList = topSongs.map(({ teamSong, score }) => {
		const song: SongCardSong = {
			name: teamSong.name,
			artist: teamSong.artist,
			artworkUrl: teamSong.artworkUrl,
			recordedKey: teamSong.recordedKey,
			recommendedKey: teamSong.recommendedKey,
			defaultKey: teamSong.defaultKey,
			rotationStatus: teamSong.rotationStatus,
			analysisStatus: teamSong.analysisStatus,
			feedback: {
				overallScore: score,
				teamSongFeedbackReports: [],
			},
		};

		const handleClick = () => {
			fireOpenSong({ teamSongId: teamSong.id, variant });
			openSong({
				teamSongId: teamSong.id,
				viewOnly: !isSelf,
			});
		};

		if (isDashboard) {
			return (
				<SongCard
					key={teamSong.id}
					song={song}
					displayOverallScore={true}
					displayKey={false}
					displayTrend={false}
					hasHover={true}
					negativeSpacing={true}
					onClick={handleClick}
				/>
			);
		}

		return (
			<div key={teamSong.id} className={styles['song-wrapper']}>
				<SongCard
					className={styles['song-item']}
					song={song}
					displayOverallScore={true}
					displayTrend={false}
					displayKey={false}
					onClick={handleClick}
				/>
			</div>
		);
	});

	if (isDashboard) {
		return (
			<div className={styles['root']}>
				<div className={styles['title-row']}>
					<div className={styles['title']}>Your top songs</div>
				</div>
				<div className={styles['songs-list']}>{songList}</div>
			</div>
		);
	}

	return (
		<>
			<SettingSectionTitle type="title" title="Top songs" />
			<div className={styles['section-card']}>{songList}</div>
		</>
	);
};
